import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getPublicApiBase } from '../utils/publicApi';
import { resolveBrandingLogoImageUrl } from '../utils/publicAssetUrl';
import { parseBrandingStyle } from '../utils/brandingStyles';

const AppBrandingContext = createContext(null);

const DEFAULT_COMPANY = 'ERP Consulting';

function parsePayload(data) {
  if (!data || typeof data !== 'object') return null;
  const trim = (v) => {
    const s = v != null ? String(v).trim() : '';
    return s || null;
  };
  return {
    portalLogoPath: trim(data.portalLogoPath),
    companyName: trim(data.companyName),
    companyTagline: trim(data.companyTagline),
    footerCopyright: trim(data.footerCopyright),
    companyNameStyle: parseBrandingStyle(data.companyNameStyle),
    companyTaglineStyle: parseBrandingStyle(data.companyTaglineStyle),
    companyTaglineStyleApp: parseBrandingStyle(data.companyTaglineStyleApp)
  };
}

/**
 * Tenant branding from Settings → Branding, loaded via public API (no JWT).
 * Wrap the app so login, signup, sidebar, and footers stay in sync after admin updates.
 */
export function AppBrandingProvider({ children }) {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const base = getPublicApiBase().replace(/\/+$/, '');
      const r = await axios.get(`${base}/branding`);
      setRaw(parsePayload(r.data?.data));
    } catch {
      setRaw(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener('erp4c-branding-updated', onUpdate);
    return () => window.removeEventListener('erp4c-branding-updated', onUpdate);
  }, [load]);

  const value = useMemo(() => {
    const b = raw || {};
    const year = new Date().getFullYear();
    const displayCompanyName = b.companyName || DEFAULT_COMPANY;
    const logoImageUrl = b.portalLogoPath ? resolveBrandingLogoImageUrl(b.portalLogoPath) : null;
    const footerText =
      b.footerCopyright ||
      `© ${year} ${displayCompanyName}. All rights reserved.`;

    return {
      loading,
      /** Raw fields from API */
      portalLogoPath: b.portalLogoPath,
      companyName: b.companyName,
      companyTagline: b.companyTagline,
      footerCopyright: b.footerCopyright,
      companyNameStyle: b.companyNameStyle,
      companyTaglineStyle: b.companyTaglineStyle,
      /** In-app (sidebar) tagline style — separate from public/front-page style */
      companyTaglineStyleApp: b.companyTaglineStyleApp,
      /** Resolved for UI */
      logoImageUrl,
      displayCompanyName,
      /** Tagline under company name in sidebar; omit if empty */
      sidebarTagline: b.companyTagline || null,
      /** Login/signup/sidebar footer */
      footerText,
      /** Short line for narrow sidebar footer */
      footerTextShort: b.footerCopyright || `© ${year} ${displayCompanyName}`
    };
  }, [raw, loading]);

  return <AppBrandingContext.Provider value={value}>{children}</AppBrandingContext.Provider>;
}

export function useAppBranding() {
  const ctx = useContext(AppBrandingContext);
  if (!ctx) {
    throw new Error('useAppBranding must be used within AppBrandingProvider');
  }
  return ctx;
}

export function useAppBrandingOptional() {
  return useContext(AppBrandingContext);
}
